import { cn } from '@/shared/lib/cn';
import type { CleaningRangeSummaryResponse } from '@/shared/api';
import { CleaningOperationalTaskChip } from './CleaningOperationalTaskChip';
import { dayLabel } from './cleaningCalendarHelpers';
import type { OperationalApartmentRow } from './cleaningOperationalRows';

interface CleaningWeekGridProps {
  rows: OperationalApartmentRow[];
  weekDates: string[];
  todayIso: string;
  selectedId?: string | null;
  onSelect?: (task: CleaningRangeSummaryResponse, trigger: HTMLElement) => void;
}

/** Breite Fläche: Apartment-Zeilen × 7 Tage, erste Spalte fest, heute als helle Spalte. */
export function CleaningWeekGrid({
  rows,
  weekDates,
  todayIso,
  selectedId,
  onSelect,
}: CleaningWeekGridProps) {
  return (
    <div className="border-hairline rounded-tile overflow-x-auto border bg-white">
      <div className="min-w-180 grid grid-cols-[160px_repeat(7,minmax(0,1fr))]">
        <div className="border-hairline text-ink-soft sticky left-0 z-10 border-b border-r bg-white px-3 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">
          Apartment
        </div>
        {weekDates.map((date) => {
          const { weekday, day } = dayLabel(date);
          const isToday = date === todayIso;
          return (
            <div
              key={date}
              aria-current={isToday ? 'date' : undefined}
              className={cn(
                'border-hairline border-b border-l px-2 py-2 text-center',
                isToday && 'bg-forest-soft',
              )}
            >
              <div className="text-ink-soft text-[11px] font-semibold uppercase">{weekday}</div>
              <div className={cn('font-serif text-base font-bold', isToday && 'text-forest-deep')}>
                {day}
              </div>
            </div>
          );
        })}

        {rows.map((row) => (
          <div key={row.propertyId} className="contents">
            <div className="border-hairline sticky left-0 z-10 flex items-center border-b border-r bg-white px-3 py-2 font-serif text-base font-bold">
              <span className="truncate">{row.name}</span>
            </div>
            {weekDates.map((date) => {
              const tasks = row.byDate.get(date) ?? [];
              return (
                <div
                  key={date}
                  className={cn(
                    'border-hairline min-h-13 space-y-1 border-b border-l p-1.5',
                    date === todayIso && 'bg-forest-soft/50',
                  )}
                >
                  {tasks.map((t) => (
                    <CleaningOperationalTaskChip
                      key={t.id}
                      task={t}
                      selected={t.id === selectedId}
                      onSelect={onSelect}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
